import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

const DocumentUpload = ({ onDocumentProcessed }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [urlInput, setUrlInput] = useState('');
    const [processingStatus, setProcessingStatus] = useState('');

    const onDrop = useCallback(async (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;

        const file = acceptedFiles[0];
        await processFile(file);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'text/plain': ['.txt'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/zip': ['.zip']
        },
        maxFiles: 1,
        maxSize: 100 * 1024 * 1024 // 100MB
    });

    const processFile = async (file) => {
        setIsProcessing(true);
        setUploadProgress(0);
        setProcessingStatus('Uploading file...');

        try {
            const formData = new FormData();
            formData.append('document', file);

            const response = await fetch('/upload', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error(`Upload failed: ${response.statusText}`);
            }

            setProcessingStatus('Processing document...');
            const result = await response.json();
            
            setProcessingStatus('Extracting entities...');
            // The backend should handle entity extraction automatically
            
            setProcessingStatus('Complete!');
            onDocumentProcessed(result);
            
        } catch (error) {
            console.error('File processing error:', error);
            setProcessingStatus(`Error: ${error.message}`);
        } finally {
            setIsProcessing(false);
            setUploadProgress(0);
            setTimeout(() => setProcessingStatus(''), 3000);
        }
    };

    const processUrl = async () => {
        if (!urlInput.trim()) return;

        setIsProcessing(true);
        setProcessingStatus('Fetching URL content...');

        try {
            const response = await fetch('/process-url', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ url: urlInput.trim() })
            });

            if (!response.ok) {
                throw new Error(`URL processing failed: ${response.statusText}`);
            }

            setProcessingStatus('Processing content...');
            const result = await response.json();
            
            setProcessingStatus('Complete!');
            onDocumentProcessed(result);
            setUrlInput('');
            
        } catch (error) {
            console.error('URL processing error:', error);
            setProcessingStatus(`Error: ${error.message}`);
        } finally {
            setIsProcessing(false);
            setTimeout(() => setProcessingStatus(''), 3000);
        }
    };

    const handleUrlKeyPress = (e) => {
        if (e.key === 'Enter') {
            processUrl();
        }
    };

    return (
        <div className="row">
            <div className="col-md-6">
                <div className="card h-100">
                    <div className="card-header">
                        <h5 className="mb-0">
                            <i className="fas fa-file-upload me-2"></i>
                            File Upload
                        </h5>
                    </div>
                    <div className="card-body">
                        <div
                            {...getRootProps()}
                            className={`dropzone border-2 border-dashed rounded p-4 text-center ${
                                isDragActive ? 'border-primary bg-light' : 'border-secondary'
                            } ${isProcessing ? 'disabled' : ''}`}
                            style={{ minHeight: '200px', cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                        >
                            <input {...getInputProps()} disabled={isProcessing} />
                            
                            {isProcessing ? (
                                <div>
                                    <div className="spinner-border text-primary mb-3" role="status">
                                        <span className="visually-hidden">Processing...</span>
                                    </div>
                                    <p className="mb-2">{processingStatus}</p>
                                    {uploadProgress > 0 && (
                                        <div className="progress mb-2">
                                            <div
                                                className="progress-bar"
                                                role="progressbar"
                                                style={{ width: `${uploadProgress}%` }}
                                                aria-valuenow={uploadProgress}
                                                aria-valuemin="0"
                                                aria-valuemax="100"
                                            >
                                                {uploadProgress}%
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : isDragActive ? (
                                <div>
                                    <i className="fas fa-cloud-upload-alt fa-3x text-primary mb-3"></i>
                                    <p className="mb-0">Drop the file here...</p>
                                </div>
                            ) : (
                                <div>
                                    <i className="fas fa-file-upload fa-3x text-muted mb-3"></i>
                                    <p className="mb-2">
                                        Drag & drop a document here, or click to select
                                    </p>
                                    <p className="small text-muted mb-0">
                                        Supports: PDF, TXT, DOC, DOCX, ZIP (max 100MB)
                                    </p>
                                </div>
                            )}
                        </div>
                        
                        {processingStatus && !isProcessing && (
                            <div className={`alert mt-3 ${
                                processingStatus.startsWith('Error') ? 'alert-danger' : 'alert-success'
                            }`}>
                                {processingStatus}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            <div className="col-md-6">
                <div className="card h-100">
                    <div className="card-header">
                        <h5 className="mb-0">
                            <i className="fas fa-link me-2"></i>
                            URL Processing
                        </h5>
                    </div>
                    <div className="card-body">
                        <p className="text-muted mb-3">
                            Enter a URL to fetch and analyze web content for cybersecurity entities.
                        </p>
                        
                        <div className="input-group mb-3">
                            <span className="input-group-text">
                                <i className="fas fa-globe"></i>
                            </span>
                            <input
                                type="url"
                                className="form-control"
                                placeholder="https://example.com/threat-report"
                                value={urlInput}
                                onChange={(e) => setUrlInput(e.target.value)}
                                onKeyPress={handleUrlKeyPress}
                                disabled={isProcessing}
                            />
                            <button
                                className="btn btn-primary"
                                onClick={processUrl}
                                disabled={isProcessing || !urlInput.trim()}
                            >
                                {isProcessing ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                ) : (
                                    <i className="fas fa-download"></i>
                                )}
                            </button>
                        </div>
                        
                        <div className="alert alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            <strong>Supported Sources:</strong>
                            <ul className="mb-0 mt-2">
                                <li>Threat intelligence reports</li>
                                <li>Security advisories</li>
                                <li>CVE databases</li>
                                <li>MITRE ATT&CK documentation</li>
                                <li>Security blog posts</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentUpload;
